import React from 'react';
import { Truck, Upload, ArrowBigLeft, Plus, X, FileText, Users, RefreshCw } from 'lucide-react';
import '../../../../styles/components/vehicle-register.scss';
import { toast } from 'react-toastify';
import i18n from '../../../../i18n';
import { getLoggedUser } from '../../../../state/user';
import { createSitePassRequest, getVehicleType } from './services/sitePassRequest';
import { Link } from 'react-router-dom';

class BulkVehicleReg extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      vehicles: [
        {
          id: 1,
          type: 'opal',
          vehicle_number: '',
          trailer_number: '',
          vehicle_type_id: '', // Changed from vehicle_type to vehicle_type_id
          vehicle_model: '',
          capacity: '',
          mulkiya_expiry_date: '',
          ras_expiry_date: '',
          mulkiyaDocuments: [],
          fitnessCertificates: [],
          vehiclePhotos: [],
          drivers: [
            {
              id: 1,
              name: '',
              phone: '',
              date_of_birth: '',
              ddExpiryDate: '',
              opal_id: '',
              ivms_url: '', // IVMS fields moved to driver
              ivms_username: '', // IVMS fields moved to driver
              ivms_password: '', // IVMS fields moved to driver
              resident_card_number: '',
              license_number: '',
              license_expiry_date: '',
              ftwDocument: null,
              idCopy: null,
              errors: {},
            },
          ],
          errors: {},
        },
      ],
      // Vehicle types moved to component level (not per vehicle)
      vehicleTypes: [],
      loadingVehicleTypes: false,
      isSubmitting: false,
    };
  }

  generateId = () => Date.now() + Math.floor(Math.random() * 1000);

  componentDidMount() {
    const params = new URLSearchParams(window.location.search);
    let urlType = params.get('type');
    const validTypes = ['opal', 'non-opal'];

    if (urlType && validTypes.includes(urlType)) {
      this.setState((prevState) => ({
        globalVehicleType: urlType,
        vehicles: prevState.vehicles.map((vehicle) => ({
          ...vehicle,
          type: urlType,
        })),
      }));

      const url = new URL(window.location);
      url.searchParams.set('type', urlType);
      window.history.replaceState({}, '', url);
    } else {
      const defaultType = 'opal';
      const url = new URL(window.location);
      url.searchParams.set('type', defaultType);
      window.history.replaceState({}, '', url);
    }

    this.fetchVehicleType();
  }

  fetchVehicleType = async () => {
    this.setState({ loadingVehicleTypes: true });

    try {
      const filters = {};
      const response = await getVehicleType(filters);

      if (response.success && response.data) {
        const vehicleTypes = response.data.map((vehicleType) => ({
          id: vehicleType.id,
          value: vehicleType.id,
          label: vehicleType.name,
        }));

        this.setState({
          vehicleTypes,
          loadingVehicleTypes: false,
        });
      } else {
        toast.error(response.message || 'Failed to fetch vehicle types');
        this.setState({ loadingVehicleTypes: false });
      }
    } catch (error) {
      console.error('Error fetching vehicle types:', error);
      toast.error('Error loading vehicle types');
      this.setState({ loadingVehicleTypes: false });
    }
  };

  // ========== VEHICLE MANAGEMENT ==========
  addVehicle = () => {
    const newVehicle = {
      id: this.generateId(),
      type: 'opal',
      vehicle_number: '',
      trailer_number: '',
      vehicle_type_id: '',
      vehicle_model: '',
      capacity: '',
      mulkiya_expiry_date: '',
      ras_expiry_date: '',
      mulkiyaDocuments: [],
      fitnessCertificates: [],
      vehiclePhotos: [],
      drivers: [
        {
          id: this.generateId(),
          name: '',
          phone: '',
          ivms_url: '', // IVMS fields in driver
          ivms_username: '', // IVMS fields in driver
          ivms_password: '', // IVMS fields in driver
          ddExpiryDate: '',
          resident_card_number: '',
          date_of_birth: '',
          license_number: '',
          license_expiry_date: '',
          ftwDocument: null,
          idCopy: null,
          errors: {},
        },
      ],
      errors: {},
    };

    this.setState((prevState) => ({
      vehicles: [...prevState.vehicles, newVehicle],
    }));
  };

  removeVehicle = (vehicleId) => {
    if (this.state.vehicles.length <= 1) {
      toast.error('At least one vehicle is required');
      return;
    }

    this.setState((prevState) => ({
      vehicles: prevState.vehicles.filter((vehicle) => vehicle.id !== vehicleId),
    }));
  };

  // ========== VEHICLE FIELD HANDLERS ==========
  handleVehicleInputChange = (vehicleId, field, value) => {
    this.setState((prevState) => ({
      vehicles: prevState.vehicles.map((vehicle) =>
        vehicle.id === vehicleId
          ? {
              ...vehicle,
              [field]: value,
              errors: {
                ...vehicle.errors,
                [field]: '',
              },
            }
          : vehicle
      ),
    }));
  };

  handleVehicleTypeSelectChange = (vehicleId, value) => {
    this.setState((prevState) => ({
      vehicles: prevState.vehicles.map((vehicle) =>
        vehicle.id === vehicleId
          ? {
              ...vehicle,
              vehicle_type_id: value,
              errors: {
                ...vehicle.errors,
                vehicle_type_id: '',
              },
            }
          : vehicle
      ),
    }));
  };

  handleVehicleTypeChange = (vehicleId, value) => {
    this.setState((prevState) => ({
      vehicles: prevState.vehicles.map((vehicle) =>
        vehicle.id === vehicleId
          ? {
              ...vehicle,
              type: value,
              errors: {
                ...vehicle.errors,
                ras_expiry_date: '',
                // IVMS errors removed from vehicle
              },
            }
          : vehicle
      ),
    }));
  };

  handleVehicleMultipleFileUpload = (vehicleId, field, event) => {
    const files = Array.from(event.target.files);
    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      if (file.size > 6 * 1024 * 1024) {
        errors.push(`${file.name}: File size should be less than 6MB`);
      } else if (!['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(file.type)) {
        errors.push(`${file.name}: Only PDF, JPG, PNG files are allowed`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      toast.error(errors.join(', '));
    }

    if (validFiles.length > 0) {
      this.setState((prevState) => ({
        vehicles: prevState.vehicles.map((vehicle) =>
          vehicle.id === vehicleId
            ? {
                ...vehicle,
                [field]: [...vehicle[field], ...validFiles],
              }
            : vehicle
        ),
      }));
    }
  };

  removeVehicleFile = (vehicleId, field, index) => {
    this.setState((prevState) => ({
      vehicles: prevState.vehicles.map((vehicle) =>
        vehicle.id === vehicleId
          ? {
              ...vehicle,
              [field]: vehicle[field].filter((_, i) => i !== index),
            }
          : vehicle
      ),
    }));
  };

  // ========== DRIVER MANAGEMENT ==========
  addDriverToVehicle = (vehicleId) => {
    const newDriver = {
      id: this.generateId(),
      name: '',
      phone: '',
      date_of_birth: '',
      ddExpiryDate: '',
      resident_card_number: '',
      license_number: '',
      license_expiry_date: '',
      opal_id: '',
      ivms_url: '', // IVMS fields in driver
      ivms_username: '', // IVMS fields in driver
      ivms_password: '', // IVMS fields in driver
      ftwDocument: null,
      idCopy: null,
      errors: {},
    };

    this.setState((prevState) => ({
      vehicles: prevState.vehicles.map((vehicle) =>
        vehicle.id === vehicleId
          ? {
              ...vehicle,
              drivers: [...vehicle.drivers, newDriver],
            }
          : vehicle
      ),
    }));
  };

  removeDriverFromVehicle = (vehicleId, driverId) => {
    this.setState((prevState) => ({
      vehicles: prevState.vehicles.map((vehicle) =>
        vehicle.id === vehicleId
          ? {
              ...vehicle,
              drivers: vehicle.drivers.filter((driver) => driver.id !== driverId),
            }
          : vehicle
      ),
    }));
  };

  // ========== DRIVER FIELD HANDLERS ==========
  handleDriverInputChange = (vehicleId, driverId, field, value) => {
    this.setState((prevState) => ({
      vehicles: prevState.vehicles.map((vehicle) =>
        vehicle.id === vehicleId
          ? {
              ...vehicle,
              drivers: vehicle.drivers.map((driver) =>
                driver.id === driverId
                  ? {
                      ...driver,
                      [field]: value,
                      errors: {
                        ...driver.errors,
                        [field]: '',
                      },
                    }
                  : driver
              ),
            }
          : vehicle
      ),
    }));
  };

  handleDriverFileUpload = (vehicleId, driverId, field, event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 6 * 1024 * 1024) {
      this.setState((prevState) => ({
        vehicles: prevState.vehicles.map((vehicle) =>
          vehicle.id === vehicleId
            ? {
                ...vehicle,
                drivers: vehicle.drivers.map((driver) =>
                  driver.id === driverId
                    ? {
                        ...driver,
                        errors: {
                          ...driver.errors,
                          [field]: 'File size should be less than 6MB',
                        },
                      }
                    : driver
                ),
              }
            : vehicle
        ),
      }));
      return;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      this.setState((prevState) => ({
        vehicles: prevState.vehicles.map((vehicle) =>
          vehicle.id === vehicleId
            ? {
                ...vehicle,
                drivers: vehicle.drivers.map((driver) =>
                  driver.id === driverId
                    ? {
                        ...driver,
                        errors: {
                          ...driver.errors,
                          [field]: 'Only PDF, JPG, PNG files are allowed',
                        },
                      }
                    : driver
                ),
              }
            : vehicle
        ),
      }));
      return;
    }

    this.setState((prevState) => ({
      vehicles: prevState.vehicles.map((vehicle) =>
        vehicle.id === vehicleId
          ? {
              ...vehicle,
              drivers: vehicle.drivers.map((driver) =>
                driver.id === driverId
                  ? {
                      ...driver,
                      [field]: file,
                      errors: {
                        ...driver.errors,
                        [field]: '',
                      },
                    }
                  : driver
              ),
            }
          : vehicle
      ),
    }));
  };

  // ========== VALIDATION ==========
  validateAll = () => {
    const today = new Date();
    const isFutureDate = (dateString) => dateString && new Date(dateString) > today;

    let allValid = true;

    const updatedVehicles = this.state.vehicles.map((vehicle) => {
      const vehicleErrors = {};
      let vehicleHasErrors = false;

      // Vehicle Information Validation
      if (!vehicle.vehicle_number.trim()) {
        vehicleErrors.vehicle_number = 'Vehicle number is required';
        vehicleHasErrors = true;
      }

      if (!vehicle.vehicle_type_id) {
        // Changed to vehicle_type_id
        vehicleErrors.vehicle_type_id = 'Vehicle type is required';
        vehicleHasErrors = true;
      }

      // if (!vehicle.vehicle_model.trim()) {
      //   vehicleErrors.vehicle_model = 'Model & Make is required';
      //   vehicleHasErrors = true;
      // }

      if (!vehicle.capacity.trim() || !/^\d+(\.\d+)?$/.test(vehicle.capacity)) {
        vehicleErrors.capacity = 'Enter a valid capacity in kg';
        vehicleHasErrors = true;
      }

      if (!vehicle.mulkiya_expiry_date || !isFutureDate(vehicle.mulkiya_expiry_date)) {
        vehicleErrors.mulkiya_expiry_date = 'Valid mulkiya expiry date is required';
        vehicleHasErrors = true;
      }

      // Documents Validation
      // if (vehicle.mulkiyaDocuments.length === 0) {
      //   vehicleErrors.mulkiyaDocuments = 'At least one mulkiya document is required';
      //   vehicleHasErrors = true;
      // }

      // if (vehicle.fitnessCertificates.length === 0) {
      //   vehicleErrors.fitnessCertificates = 'At least one fitness certificate is required';
      //   vehicleHasErrors = true;
      // }

      // OPAL Validation for vehicle
      if (vehicle.type === 'opal') {
        if (!vehicle.ras_expiry_date || !isFutureDate(vehicle.ras_expiry_date)) {
          vehicleErrors.ras_expiry_date = 'Valid RAS expiry date is required';
          vehicleHasErrors = true;
        }
        // IVMS validation removed from vehicle
      }

      // Validate drivers for this vehicle
      const updatedDrivers = vehicle.drivers.map((driver) => {
        const driverErrors = {};
        let driverHasErrors = false;

        if (!driver.name.trim()) {
          driverErrors.name = 'Driver name is required';
          driverHasErrors = true;
        }

        // if (!driver.phone.trim()) {
        //   driverErrors.phone = 'Contact number is required';
        //   driverHasErrors = true;
        // }

        if (!driver.license_number.trim()) {
          driverErrors.license_number = 'License number is required';
          driverHasErrors = true;
        }

        if (!driver.license_expiry_date || !isFutureDate(driver.license_expiry_date)) {
          driverErrors.license_expiry_date = 'Valid license expiry date is required';
          driverHasErrors = true;
        }

        if (!driver.resident_card_number.trim()) {
          driverErrors.resident_card_number = 'Resident ID Number is required';
          driverHasErrors = true;
        }

        // OPAL Validation for driver (including IVMS)
        if (vehicle.type === 'opal') {
          if (!driver.ddExpiryDate || !isFutureDate(driver.ddExpiryDate)) {
            driverErrors.ddExpiryDate = 'Valid DD expiry date is required';
            driverHasErrors = true;
          }

          // IVMS validation moved to driver
          if (!driver.ivms_url.trim()) {
            driverErrors.ivms_url = 'IVMS URL is required';
            driverHasErrors = true;
          }
          if (!driver.ivms_username.trim()) {
            driverErrors.ivms_username = 'IVMS username is required';
            driverHasErrors = true;
          }
          if (!driver.ivms_password.trim()) {
            driverErrors.ivms_password = 'IVMS password is required';
            driverHasErrors = true;
          }
        }

        // if (!driver.ftwDocument) {
        //   driverErrors.ftwDocument = 'FTW document is required';
        //   driverHasErrors = true;
        // }

        if (!driver.idCopy) {
          driverErrors.idCopy = 'ID copy is required';
          driverHasErrors = true;
        }

        if (driverHasErrors) {
          vehicleHasErrors = true;
          allValid = false;
        }

        return {
          ...driver,
          errors: driverErrors,
        };
      });

      if (vehicleHasErrors) {
        allValid = false;
      }

      return {
        ...vehicle,
        errors: vehicleErrors,
        drivers: updatedDrivers,
      };
    });

    this.setState({ vehicles: updatedVehicles });
    return allValid;
  };

  // ========== SUBMISSION ==========
  handleSave = async () => {
    // if (!this.validateAll()) {
    //   toast.error('Please fix all errors before submitting');
    //   return;
    // }

    this.setState({ isSubmitting: true });

    try {
      const formData = new FormData();
      const user = getLoggedUser();

      // Add each vehicle with its data
      this.state.vehicles.forEach((vehicle, vehicleIndex) => {
        // Add vehicle basic information
        formData.append(`vehicles[${vehicleIndex}][vehicle_number]`, vehicle.vehicle_number);
        formData.append(`vehicles[${vehicleIndex}][trailer_number]`, vehicle.trailer_number);
        // formData.append(`vehicles[${vehicleIndex}][vehicle_model]`, vehicle.vehicle_model);
        formData.append(`vehicles[${vehicleIndex}][vehicle_type_id]`, vehicle.vehicle_type_id); // Changed to vehicle_type_id
        formData.append(`vehicles[${vehicleIndex}][capacity]`, vehicle.capacity);
        formData.append(
          `vehicles[${vehicleIndex}][mulkiya_expiry_date]`,
          vehicle.mulkiya_expiry_date
        );
        formData.append(`vehicles[${vehicleIndex}][status]`, 'P');
        formData.append(`vehicles[${vehicleIndex}][opal]`, vehicle.type === 'opal' ? '1' : '0');

        // OPAL specific fields for vehicle
        if (vehicle.type === 'opal') {
          formData.append(
            `vehicles[${vehicleIndex}][ras_expiry_date]`,
            vehicle.ras_expiry_date || ''
          );
          // IVMS fields NOT added to vehicle
        }

        // Add vehicle documents
        // vehicle.mulkiyaDocuments.forEach((file, docIndex) => {
        //   formData.append(`vehicles[${vehicleIndex}][documents][${docIndex}][document_type]`, 'mulkiya');
        //   formData.append(`vehicles[${vehicleIndex}][documents][${docIndex}][file]`, file);
        // });

        // const fitnessDocIndex = vehicle.mulkiyaDocuments.length;
        // vehicle.fitnessCertificates.forEach((file, index) => {
        //   formData.append(`vehicles[${vehicleIndex}][documents][${fitnessDocIndex + index}][document_type]`, 'insurance');
        //   formData.append(`vehicles[${vehicleIndex}][documents][${fitnessDocIndex + index}][file]`, file);
        // });

        const photoDocIndex = vehicle.mulkiyaDocuments.length + vehicle.fitnessCertificates.length;
        vehicle.vehiclePhotos.forEach((file, index) => {
          formData.append(
            `vehicles[${vehicleIndex}][documents][${photoDocIndex + index}][document_type]`,
            'registration'
          );
          formData.append(
            `vehicles[${vehicleIndex}][documents][${photoDocIndex + index}][file]`,
            file
          );
        });

        // Add driver data
        if (vehicle.drivers.length > 0) {
          const driver = vehicle.drivers[0];

          formData.append(`vehicles[${vehicleIndex}][driver][name]`, driver.name);
          formData.append(`vehicles[${vehicleIndex}][driver][phone]`, driver.phone);
          formData.append(
            `vehicles[${vehicleIndex}][driver][resident_card_number]`,
            driver.resident_card_number
          );
          formData.append(
            `vehicles[${vehicleIndex}][driver][license_number]`,
            driver.license_number
          );
          formData.append(
            `vehicles[${vehicleIndex}][driver][license_expiry_date]`,
            driver.license_expiry_date
          );
          formData.append(`vehicles[${vehicleIndex}][driver][status]`, 'P');
          formData.append(
            `vehicles[${vehicleIndex}][driver][opal]`,
            vehicle.type === 'opal' ? '1' : '0'
          );

          // Optional fields
          if (driver.date_of_birth) {
            formData.append(
              `vehicles[${vehicleIndex}][driver][date_of_birth]`,
              driver.date_of_birth
            );
          }

          if (driver.opal_id) {
            formData.append(`vehicles[${vehicleIndex}][driver][opal_id]`, driver.opal_id);
          }

          // OPAL specific fields for driver (including IVMS)
          if (vehicle.type === 'opal' && driver.ddExpiryDate) {
            formData.append(
              `vehicles[${vehicleIndex}][driver][dd_certificate]`,
              driver.ddExpiryDate
            );
            formData.append(
              `vehicles[${vehicleIndex}][driver][dd_certification_expiry]`,
              driver.ddExpiryDate
            );

            // Add IVMS fields to driver
            formData.append(`vehicles[${vehicleIndex}][driver][ivms_url]`, driver.ivms_url || '');
            formData.append(
              `vehicles[${vehicleIndex}][driver][ivms_username]`,
              driver.ivms_username || ''
            );
            formData.append(
              `vehicles[${vehicleIndex}][driver][ivms_password]`,
              driver.ivms_password || ''
            );
          }

          // Add driver documents
          let driverDocIndex = 0;
          if (driver.ftwDocument) {
            formData.append(
              `vehicles[${vehicleIndex}][driver][driver_documents][${driverDocIndex}][document_type]`,
              'ftw'
            );
            formData.append(
              `vehicles[${vehicleIndex}][driver][driver_documents][${driverDocIndex}][file]`,
              driver.ftwDocument
            );
            driverDocIndex++;
          }

          if (driver.idCopy) {
            formData.append(
              `vehicles[${vehicleIndex}][driver][driver_documents][${driverDocIndex}][document_type]`,
              'id_copy'
            );
            formData.append(
              `vehicles[${vehicleIndex}][driver][driver_documents][${driverDocIndex}][file]`,
              driver.idCopy
            );
          }
        }
      });

      // Add company_id if user has it
      if (user && user.company_id) {
        formData.append('company_id', user.company_id);
      }

      const response = await createSitePassRequest(formData, true);

      if (!response.success) {
        if (response.status === 422 && response.errors) {
          console.error('Validation errors:', response.errors);
          let errorMessage = 'Please fix the following errors:\n';
          Object.entries(response.errors).forEach(([vehicleIndex, vehicleErrors]) => {
            errorMessage += `\nVehicle ${parseInt(vehicleIndex) + 1}:\n`;
            Object.entries(vehicleErrors).forEach(([field, errors]) => {
              errorMessage += `  ${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}\n`;
            });
          });
          toast.error(errorMessage);
        } else {
          throw new Error(response.message || 'Failed to submit form');
        }
        return;
      }

      if (response.success) {
        toast.success(`Successfully registered ${response.data.created_count} vehicle(s)!`);
      } else {
        throw new Error(response.message || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Failed to submit form');
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  // ========== RENDER HELPERS ==========
  renderVehicleFormGroup = (vehicleId, label, field, children, required = false) => (
    <div className="form-group" data-field={`vehicle-${vehicleId}-${field}`}>
      <label>
        {label}{' '}
        {required && (
          <span className="required-star" style={{ color: 'red' }}>
            *
          </span>
        )}
      </label>
      {children}
      {this.state.vehicles.find((v) => v.id === vehicleId)?.errors[field] && (
        <span className="error-message">
          {this.state.vehicles.find((v) => v.id === vehicleId).errors[field]}
        </span>
      )}
    </div>
  );

  renderDriverFormGroup = (vehicleId, driverId, label, field, children, required = false) => (
    <div className="form-group" data-field={`vehicle-${vehicleId}-driver-${driverId}-${field}`}>
      <label>
        {label}{' '}
        {required && (
          <span className="required-star" style={{ color: 'red' }}>
            *
          </span>
        )}
      </label>
      {children}
      {this.state.vehicles.find((v) => v.id === vehicleId)?.drivers.find((d) => d.id === driverId)
        ?.errors[field] && (
        <span className="error-message">
          {
            this.state.vehicles
              .find((v) => v.id === vehicleId)
              .drivers.find((d) => d.id === driverId).errors[field]
          }
        </span>
      )}
    </div>
  );

  renderFilePreview = (files, vehicleId, field) => (
    <div className="file-previews">
      {files.map((file, index) => (
        <div key={index} className="file-preview">
          <div className="file-info">
            <FileText size={16} />
            <span>{file.name}</span>
            <span className="file-size">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
          </div>
          <button
            type="button"
            className="button1 button1-secondary"
            onClick={() => this.removeVehicleFile(vehicleId, field, index)}
            disabled={this.state.isSubmitting}
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );

  renderVehicleMultipleFileUpload = (vehicleId, field, label, required = false) => (
    <div className="form-group" data-field={`vehicle-${vehicleId}-${field}`}>
      <label>
        {label}{' '}
        {required && (
          <span className="required-star" style={{ color: 'red' }}>
            *
          </span>
        )}
      </label>
      <div className="multiple-file-upload">
        <div
          className={`file-upload ${this.state.vehicles.find((v) => v.id === vehicleId)?.errors[field] ? 'error-border' : ''}`}
        >
          <Upload size={32} />
          <p>
            Drag & drop files or <span className="browse-link">browse</span>
          </p>
          <small>PDF, JPG, PNG Max 6MB each</small>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            multiple
            onChange={(e) => this.handleVehicleMultipleFileUpload(vehicleId, field, e)}
            disabled={this.state.isSubmitting}
          />
        </div>

        {this.state.vehicles.find((v) => v.id === vehicleId)?.[field]?.length > 0 && (
          <div className="uploaded-files">
            <h4>
              Uploaded Files ({this.state.vehicles.find((v) => v.id === vehicleId)[field].length})
            </h4>
            {this.renderFilePreview(
              this.state.vehicles.find((v) => v.id === vehicleId)[field],
              vehicleId,
              field
            )}
          </div>
        )}

        {this.state.vehicles.find((v) => v.id === vehicleId)?.errors[field] && (
          <span className="error-message">
            {this.state.vehicles.find((v) => v.id === vehicleId).errors[field]}
          </span>
        )}
      </div>
    </div>
  );

  // ========== RENDER ==========
  render() {
    const { isSubmitting, vehicles, vehicleTypes, loadingVehicleTypes } = this.state;
    const totalDrivers = vehicles.reduce((total, vehicle) => total + vehicle.drivers.length, 0);

    return (
      <React.Fragment>
        <section className="site-visit-details-page card">
          <div className="container">
            <div className="row">
              <div className="col-xl-12 col-lg-12 col-md-12">
                <main className="p-3 main-content ">
                  <div className="page-header">
                    <div>
                      <Link
                        className="back-button"
                        to={'/'}
                        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                        disabled={isSubmitting}
                      >
                        <ArrowBigLeft size={24} />
                      </Link>
                    </div>

                    <div>
                      <h1>{i18n.t('bulk_vehicle_reg')}</h1>
                    </div>

                    <div className="header-actions">{/* Empty for now */}</div>
                  </div>

                  <div className="content">
                    {/* Summary Stats */}
                    <div className="summary-stats">
                      <div className="fleet-card">
                        <Truck className="fleet-card-icon" size={24} />
                        <div className="fleet-card-title pt-3">
                          <h3>{vehicles.length}</h3>
                          <p>Vehicles</p>
                        </div>
                      </div>
                      <div className="fleet-card ">
                        <Users className="fleet-card-icon" size={24} />
                        <div className="fleet-card-title pt-3">
                          <h3>{totalDrivers}</h3>
                          <p>Total Drivers</p>
                        </div>
                      </div>
                      <div className="fleet-card">
                        <FileText size={24} />
                        <div className="fleet-card-title pt-3">
                          <h3>
                            {vehicles.reduce(
                              (total, vehicle) =>
                                total +
                                vehicle.mulkiyaDocuments.length +
                                vehicle.fitnessCertificates.length,
                              0
                            )}
                          </h3>
                          <p>Total Documents</p>
                        </div>
                      </div>
                    </div>

                    {/* Vehicle Forms */}
                    {vehicles.map((vehicle, vehicleIndex) => (
                      <div key={vehicle.id} className="vehicle-form-section">
                        <div className="vehicle-details-section">
                          <div className="form-grid">
                            <div className="form-group full-width">
                              <div className="section-header section-divider">
                                <div className="card-t1-header">
                                  <Truck size={24} className="header-icon" />
                                  <h2> Vehicle {vehicleIndex + 1}</h2>
                                </div>
                                <div style={{ display: 'flex', gap: '5px' }}>
                                  <div>
                                    {vehicles.length > 1 && (
                                      <button
                                        type="button"
                                        className="button1 button1-secondary"
                                        onClick={() => this.removeVehicle(vehicle.id)}
                                        disabled={isSubmitting}
                                      >
                                        <X size={16} />
                                        Remove Vehicle
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Vehicle Number */}
                            {this.renderVehicleFormGroup(
                              vehicle.id,
                              'Vehicle Number',
                              'vehicle_number',
                              <input
                                type="text"
                                placeholder="e.g., MH12 AB 3456"
                                value={vehicle.vehicle_number}
                                onChange={(e) =>
                                  this.handleVehicleInputChange(
                                    vehicle.id,
                                    'vehicle_number',
                                    e.target.value
                                  )
                                }
                                disabled={isSubmitting}
                                className={vehicle.errors.vehicle_number ? 'error' : ''}
                              />,
                              true
                            )}

                            {this.renderVehicleFormGroup(
                              vehicle.id,
                              'Trailer Number',
                              'trailer_number',
                              <input
                                type="text"
                                placeholder="e.g., MH12 AB 3456"
                                value={vehicle.trailer_number}
                                onChange={(e) =>
                                  this.handleVehicleInputChange(
                                    vehicle.id,
                                    'trailer_number',
                                    e.target.value
                                  )
                                }
                                disabled={isSubmitting}
                              />
                            )}

                            {/* Vehicle Type Dropdown - FIXED */}
                            <div className="form-group">
                              <label className="required">Vehicle Type</label>
                              <select
                                value={vehicle.vehicle_type_id}
                                onChange={(e) =>
                                  this.handleVehicleTypeSelectChange(vehicle.id, e.target.value)
                                }
                                className={vehicle.errors.vehicle_type_id ? 'error' : ''}
                                disabled={loadingVehicleTypes || isSubmitting}
                              >
                                <option value="">Select Vehicle Type</option>
                                {loadingVehicleTypes ? (
                                  <option value="" disabled>
                                    Loading Vehicle Types...
                                  </option>
                                ) : (
                                  vehicleTypes.map((vehicleType) => (
                                    <option key={vehicleType.id} value={vehicleType.id}>
                                      {vehicleType.label}
                                    </option>
                                  ))
                                )}
                              </select>
                              {vehicle.errors.vehicle_type_id && (
                                <span className="error-message">
                                  {vehicle.errors.vehicle_type_id}
                                </span>
                              )}
                              <button
                                type="button"
                                className="button1 button1-primary"
                                onClick={this.fetchVehicleType}
                                style={{ marginTop: '5px', fontSize: '12px', padding: '2px 8px' }}
                                disabled={loadingVehicleTypes || isSubmitting}
                              >
                                <RefreshCw size={12} /> Refresh List
                              </button>
                            </div>

                            {/* Model & Make */}
                            {/* {this.renderVehicleFormGroup(
                              vehicle.id,
                              "Model & Make",
                              "vehicle_model",
                              <input
                                type="text"
                                placeholder="e.g., Tata Signa 4825.TK"
                                value={vehicle.vehicle_model}
                                onChange={(e) => this.handleVehicleInputChange(vehicle.id, 'vehicle_model', e.target.value)}
                                disabled={isSubmitting}
                                className={vehicle.errors.vehicle_model ? 'error' : ''}
                              />,
                              true
                            )} */}

                            {/* Capacity */}
                            {this.renderVehicleFormGroup(
                              vehicle.id,
                              'Capacity (KG)',
                              'capacity',
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Enter capacity in kg"
                                value={vehicle.capacity}
                                onChange={(e) =>
                                  this.handleVehicleInputChange(
                                    vehicle.id,
                                    'capacity',
                                    e.target.value
                                  )
                                }
                                disabled={isSubmitting}
                                className={vehicle.errors.capacity ? 'error' : ''}
                              />,
                              true
                            )}

                            {/* Mulkiya Expiry Date */}
                            {this.renderVehicleFormGroup(
                              vehicle.id,
                              'Mulkiya Expiry Date',
                              'mulkiya_expiry_date',
                              <input
                                type="date"
                                value={vehicle.mulkiya_expiry_date}
                                onChange={(e) =>
                                  this.handleVehicleInputChange(
                                    vehicle.id,
                                    'mulkiya_expiry_date',
                                    e.target.value
                                  )
                                }
                                disabled={isSubmitting}
                                min={new Date().toISOString().split('T')[0]}
                                className={vehicle.errors.mulkiya_expiry_date ? 'error' : ''}
                              />,
                              true
                            )}

                            {/* OPAL Specific Fields */}
                            {vehicle.type === 'opal' && (
                              <>
                                {this.renderVehicleFormGroup(
                                  vehicle.id,
                                  'RAS Expiry Date',
                                  'ras_expiry_date',
                                  <input
                                    type="date"
                                    value={vehicle.ras_expiry_date}
                                    onChange={(e) =>
                                      this.handleVehicleInputChange(
                                        vehicle.id,
                                        'ras_expiry_date',
                                        e.target.value
                                      )
                                    }
                                    disabled={isSubmitting}
                                    min={new Date().toISOString().split('T')[0]}
                                    className={vehicle.errors.ras_expiry_date ? 'error' : ''}
                                  />,
                                  true
                                )}
                              </>
                            )}
                          </div>

                          {/* <div className="form-group full-width pt-3">
                            <div className="section-header section-divider">
                              <div className="card-t1-header">
                                <Truck size={24} className="header-icon" />
                                <h2> Vehicle Documents</h2>
                              </div>
                            </div>
                          </div> */}

                          {/* <div className="form-grid">
                            {this.renderVehicleMultipleFileUpload(
                              vehicle.id,
                              'mulkiyaDocuments',
                              'Mulkiya Documents',
                              true
                            )}

                            {this.renderVehicleMultipleFileUpload(
                              vehicle.id,
                              'fitnessCertificates',
                              'Fitness Certificates',
                              true
                            )}
                          </div> */}
                        </div>

                        {/* Drivers for this Vehicle */}
                        <div className="drivers-section">
                          <div className="form-group full-width pt-3">
                            <div className="section-header section-divider">
                              <div className="card-t1-header">
                                <Truck size={24} className="header-icon" />
                                <h2>Drivers for Vehicle {vehicleIndex + 1}</h2>
                              </div>
                            </div>
                          </div>

                          {vehicle.drivers.map((driver, driverIndex) => (
                            <div key={driver.id} className="driver-form-section">
                              <div className="driver-header">
                                <h4>Driver {driverIndex + 1}</h4>
                                {vehicle.drivers.length > 1 && (
                                  <button
                                    type="button"
                                    className="button1 button1-secondary"
                                    onClick={() =>
                                      this.removeDriverFromVehicle(vehicle.id, driver.id)
                                    }
                                    disabled={isSubmitting}
                                  >
                                    <X size={14} />
                                    Remove Driver
                                  </button>
                                )}
                              </div>

                              <div className="form-grid">
                                {/* Driver Name */}
                                {this.renderDriverFormGroup(
                                  vehicle.id,
                                  driver.id,
                                  'Full Name',
                                  'name',
                                  <input
                                    type="text"
                                    placeholder="Enter Driver Name"
                                    value={driver.name}
                                    onChange={(e) =>
                                      this.handleDriverInputChange(
                                        vehicle.id,
                                        driver.id,
                                        'name',
                                        e.target.value
                                      )
                                    }
                                    disabled={isSubmitting}
                                    className={driver.errors.name ? 'error' : ''}
                                  />,
                                  true
                                )}

                                {/* Contact Number */}
                                {this.renderDriverFormGroup(
                                  vehicle.id,
                                  driver.id,
                                  'Contact Number',
                                  'phone',
                                  <input
                                    type="tel"
                                    placeholder="e.g., +968 9123 4567"
                                    value={driver.phone}
                                    onChange={(e) =>
                                      this.handleDriverInputChange(
                                        vehicle.id,
                                        driver.id,
                                        'phone',
                                        e.target.value
                                      )
                                    }
                                    disabled={isSubmitting}
                                  />
                                )}

                                {/* License Number */}
                                {this.renderDriverFormGroup(
                                  vehicle.id,
                                  driver.id,
                                  'License Number',
                                  'license_number',
                                  <input
                                    type="text"
                                    placeholder="Enter License Number"
                                    value={driver.license_number}
                                    onChange={(e) =>
                                      this.handleDriverInputChange(
                                        vehicle.id,
                                        driver.id,
                                        'license_number',
                                        e.target.value
                                      )
                                    }
                                    disabled={isSubmitting}
                                    className={driver.errors.license_number ? 'error' : ''}
                                  />,
                                  true
                                )}

                                {/* License Expiry Date */}
                                {this.renderDriverFormGroup(
                                  vehicle.id,
                                  driver.id,
                                  'License Expiry Date',
                                  'license_expiry_date',
                                  <input
                                    type="date"
                                    value={driver.license_expiry_date}
                                    onChange={(e) =>
                                      this.handleDriverInputChange(
                                        vehicle.id,
                                        driver.id,
                                        'license_expiry_date',
                                        e.target.value
                                      )
                                    }
                                    disabled={isSubmitting}
                                    min={new Date().toISOString().split('T')[0]}
                                    className={driver.errors.license_expiry_date ? 'error' : ''}
                                  />,
                                  true
                                )}

                                {/* Date of Birth */}
                                {this.renderDriverFormGroup(
                                  vehicle.id,
                                  driver.id,
                                  'Date of Birth',
                                  'date_of_birth',
                                  <input
                                    type="date"
                                    value={driver.date_of_birth}
                                    onChange={(e) =>
                                      this.handleDriverInputChange(
                                        vehicle.id,
                                        driver.id,
                                        'date_of_birth',
                                        e.target.value
                                      )
                                    }
                                    disabled={isSubmitting}
                                    max={new Date().toISOString().split('T')[0]}
                                  />
                                )}

                                {/* OPAL ID */}
                                {vehicle.type === 'opal' &&
                                  this.renderDriverFormGroup(
                                    vehicle.id,
                                    driver.id,
                                    'OPAL ID',
                                    'opal_id',
                                    <input
                                      type="text"
                                      placeholder="Enter OPAL ID"
                                      value={driver.opal_id}
                                      onChange={(e) =>
                                        this.handleDriverInputChange(
                                          vehicle.id,
                                          driver.id,
                                          'opal_id',
                                          e.target.value
                                        )
                                      }
                                      disabled={isSubmitting}
                                    />
                                  )}

                                {/* OPAL Specific Fields for Driver */}
                                {vehicle.type === 'opal' && (
                                  <>
                                    {/* IVMS URL - MOVED TO DRIVER */}
                                    {this.renderDriverFormGroup(
                                      vehicle.id,
                                      driver.id,
                                      'IVMS URL',
                                      'ivms_url',
                                      <input
                                        type="url"
                                        placeholder="https://example.com"
                                        value={driver.ivms_url}
                                        onChange={(e) =>
                                          this.handleDriverInputChange(
                                            vehicle.id,
                                            driver.id,
                                            'ivms_url',
                                            e.target.value
                                          )
                                        }
                                        disabled={isSubmitting}
                                        className={driver.errors.ivms_url ? 'error' : ''}
                                      />,
                                      true
                                    )}

                                    {/* IVMS Username - MOVED TO DRIVER */}
                                    {this.renderDriverFormGroup(
                                      vehicle.id,
                                      driver.id,
                                      'IVMS Username',
                                      'ivms_username',
                                      <input
                                        type="text"
                                        placeholder="Enter IVMS username"
                                        value={driver.ivms_username}
                                        onChange={(e) =>
                                          this.handleDriverInputChange(
                                            vehicle.id,
                                            driver.id,
                                            'ivms_username',
                                            e.target.value
                                          )
                                        }
                                        disabled={isSubmitting}
                                        className={driver.errors.ivms_username ? 'error' : ''}
                                      />,
                                      true
                                    )}

                                    {/* IVMS Password - MOVED TO DRIVER */}
                                    {this.renderDriverFormGroup(
                                      vehicle.id,
                                      driver.id,
                                      'IVMS Password',
                                      'ivms_password',
                                      <input
                                        type="text"
                                        placeholder="Enter IVMS password"
                                        value={driver.ivms_password}
                                        onChange={(e) =>
                                          this.handleDriverInputChange(
                                            vehicle.id,
                                            driver.id,
                                            'ivms_password',
                                            e.target.value
                                          )
                                        }
                                        disabled={isSubmitting}
                                        className={driver.errors.ivms_password ? 'error' : ''}
                                      />,
                                      true
                                    )}

                                    {/* DD Expiry Date */}
                                    {this.renderDriverFormGroup(
                                      vehicle.id,
                                      driver.id,
                                      'DD Expiry Date',
                                      'ddExpiryDate',
                                      <input
                                        type="date"
                                        value={driver.ddExpiryDate}
                                        onChange={(e) =>
                                          this.handleDriverInputChange(
                                            vehicle.id,
                                            driver.id,
                                            'ddExpiryDate',
                                            e.target.value
                                          )
                                        }
                                        disabled={isSubmitting}
                                        min={new Date().toISOString().split('T')[0]}
                                        className={driver.errors.ddExpiryDate ? 'error' : ''}
                                      />,
                                      true
                                    )}
                                  </>
                                )}

                                {/* Driver Documents */}

                                <div className="form-group">
                                  <label>ID Copy *</label>
                                  <div className="file-upload">
                                    <Upload size={24} />
                                    <p>Upload ID copy</p>
                                    <small>PDF, JPG, PNG Max 6MB</small>
                                    <input
                                      type="file"
                                      accept=".pdf,.jpg,.jpeg,.png"
                                      onChange={(e) =>
                                        this.handleDriverFileUpload(
                                          vehicle.id,
                                          driver.id,
                                          'idCopy',
                                          e
                                        )
                                      }
                                      disabled={isSubmitting}
                                    />
                                    {driver.idCopy && (
                                      <div className="selected-file">
                                        Selected: {driver.idCopy.name}
                                      </div>
                                    )}
                                  </div>
                                  {driver.errors.idCopy && (
                                    <span className="error-message">{driver.errors.idCopy}</span>
                                  )}
                                </div>

                                <div className="form-group">
                                  <label>Other Doc (if any)</label>
                                  <div className="file-upload">
                                    <Upload size={24} />
                                    <p>Upload Other document</p>
                                    <small>PDF, JPG, PNG Max 6MB</small>
                                    <input
                                      type="file"
                                      accept=".pdf,.jpg,.jpeg,.png"
                                      onChange={(e) =>
                                        this.handleDriverFileUpload(
                                          vehicle.id,
                                          driver.id,
                                          'ftwDocument',
                                          e
                                        )
                                      }
                                      disabled={isSubmitting}
                                    />
                                    {driver.ftwDocument && (
                                      <div className="selected-file">
                                        Selected: {driver.ftwDocument.name}
                                      </div>
                                    )}
                                  </div>
                                  {driver.errors.ftwDocument && (
                                    <span className="error-message">
                                      {driver.errors.ftwDocument}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="page-footer">
                    <div className="footer-stats">
                      <span>
                        Total: {vehicles.length} Vehicle(s), {totalDrivers} Driver(s)
                      </span>
                    </div>
                    <div className="footer-actions">
                      <button
                        className="button1 button1-primary"
                        onClick={this.addVehicle}
                        disabled={isSubmitting}
                      >
                        <Plus size={18} />
                        Add Vehicle
                      </button>

                      <button
                        className="button1 button1-primary"
                        onClick={this.handleSave}
                        disabled={isSubmitting}
                      >
                        {isSubmitting
                          ? 'Submitting...'
                          : `Submit All (${vehicles.length} Vehicles)`}
                      </button>
                    </div>
                  </div>
                </main>
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}

export default BulkVehicleReg;
